/**
 * UniversalJoinPanel — coach-only panel that displays the permanent
 * `/join/{slug}` URL, a 4-digit PIN editor per team, a copy/share button and
 * the toggle to pause the link. Lives at the top of the Roster page so it's
 * the first thing the coach sees when looking to add players.
 */
import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useTeams } from '@/context/TeamContext';
import { useLang } from '@/context/LangContext';
import { toast } from 'sonner';
import {
  Link as LinkIcon, Copy, Share2, Lock, Pause, Play, Check, ExternalLink,
} from 'lucide-react';

export default function UniversalJoinPanel() {
  const { teams, activeTeamId, refresh } = useTeams();
  const { t } = useLang();
  const [slug, setSlug] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [editingPinFor, setEditingPinFor] = useState(null);
  const [pinDraft, setPinDraft] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get('/users/me/join-link').then(r => {
      setSlug(r.data.slug);
      setEnabled(r.data.enabled);
    }).catch(() => {});
  }, []);

  const fullUrl = slug ? `${window.location.origin}/join/${slug}` : '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success(t('ujp_link_copied'));
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t('ujp_copy_failed'));
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('ujp_share_native_title'),
          text: t('ujp_share_native_text'),
          url: fullUrl,
        });
      } catch {/* user cancelled */}
    } else {
      copy();
    }
  };

  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    try {
      await api.post('/users/me/join-link/toggle', { enabled: next });
      toast.success(next ? t('ujp_link_activated') : t('ujp_link_paused'));
    } catch (e) {
      setEnabled(!next);
      toast.error(e?.response?.data?.detail || t('ujp_link_update_error'));
    }
  };

  const savePin = async (teamId) => {
    try {
      await api.patch(`/teams/${teamId}`, { join_pin: pinDraft });
      toast.success(pinDraft ? `${t('ujp_pin_saved')}: ${pinDraft}` : t('ujp_pin_removed'));
      setEditingPinFor(null);
      setPinDraft('');
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('ujp_pin_save_error'));
    }
  };

  if (!slug) return null;

  return (
    <div className="bg-card-steel border-2 border-gold/40 rounded-lg p-4 mb-6" data-testid="universal-join-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold/80 mb-1">
            {t('ujp_eyebrow')}
          </div>
          <h3 className="font-h uppercase text-lg text-white">
            <LinkIcon className="w-4 h-4 inline me-2" /> {t('ujp_title')}
          </h3>
          <p className="text-xs text-gray-400 mt-1">
            {t('ujp_subtitle')}
          </p>
        </div>
        <button
          onClick={toggle}
          data-testid="universal-join-toggle"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-h uppercase tracking-wider border-2 ${
            enabled ? 'border-emerald-400 text-emerald-300 bg-emerald-500/10' : 'border-red-400 text-red-300 bg-red-500/10'
          }`}
        >
          {enabled ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
          {enabled ? t('ujp_active') : t('ujp_paused')}
        </button>
      </div>

      {/* URL row */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="flex-1 min-w-0 bg-navy-deep border border-gold/30 rounded px-3 py-2 font-mono text-xs text-gold break-all" data-testid="universal-join-url">
          {fullUrl}
        </div>
        <button
          onClick={copy}
          data-testid="universal-join-copy"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-gold text-navy text-xs font-h uppercase tracking-wider font-semibold hover:bg-gold-light"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {t('ujp_copy')}
        </button>
        <button
          onClick={share}
          data-testid="universal-join-share"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-gold/40 text-gold text-xs font-h uppercase tracking-wider hover:bg-gold/10"
        >
          <Share2 className="w-3.5 h-3.5" /> {t('ujp_share')}
        </button>
        <a
          href={fullUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="universal-join-preview"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-gold/30 text-gold/80 text-xs font-h uppercase tracking-wider hover:bg-gold/10"
          title={t('ujp_view_as_player_title')}
        >
          <ExternalLink className="w-3.5 h-3.5" /> {t('ujp_view')}
        </a>
      </div>

      {/* Per-team PIN editor */}
      <div className="border-t border-gold/15 pt-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold/70 mb-2 flex items-center gap-1">
          <Lock className="w-3 h-3" /> {t('ujp_pin_section_title')}
        </div>
        <div className="space-y-1.5">
          {teams.map(team => {
            const isEditing = editingPinFor === team.id;
            const currentPin = team.join_pin;
            return (
              <div key={team.id} className="flex items-center gap-2 text-sm" data-testid={`team-pin-row-${team.id}`}>
                <div className="flex-1 min-w-0 truncate font-h uppercase tracking-wider text-xs text-white">
                  {team.name}
                  {team.id === activeTeamId && <span className="ms-2 text-gold text-[10px]">{t('ujp_active_tag')}</span>}
                </div>
                {isEditing ? (
                  <>
                    <input
                      autoFocus
                      inputMode="numeric"
                      value={pinDraft}
                      onChange={e => setPinDraft(e.target.value.replace(/[^0-9]/g, '').slice(0, 8))}
                      placeholder={t('ujp_pin_placeholder')}
                      data-testid={`team-pin-input-${team.id}`}
                      className="w-32 bg-navy-deep border border-gold/40 rounded px-2 py-1 text-xs text-white outline-none font-mono tracking-widest"
                    />
                    <button
                      onClick={() => savePin(team.id)}
                      data-testid={`team-pin-save-${team.id}`}
                      className="px-2 py-1 rounded bg-gold text-navy text-[11px] font-bold uppercase tracking-wider"
                    >
                      OK
                    </button>
                    <button
                      onClick={() => { setEditingPinFor(null); setPinDraft(''); }}
                      className="text-gray-400 text-[11px] hover:text-white px-1"
                    >
                      ✕
                    </button>
                  </>
                ) : (
                  <>
                    <div className="font-mono text-xs text-gold tracking-widest">
                      {currentPin ? `••${currentPin.slice(-2)}` : t('ujp_no_pin')}
                    </div>
                    <button
                      onClick={() => { setEditingPinFor(team.id); setPinDraft(currentPin || ''); }}
                      data-testid={`team-pin-edit-${team.id}`}
                      className="text-gold/70 hover:text-gold text-[11px] font-h uppercase tracking-wider"
                    >
                      {currentPin ? t('ujp_change') : t('ujp_add')}
                    </button>
                    {currentPin && (
                      <button
                        onClick={() => { setEditingPinFor(team.id); setPinDraft(''); setTimeout(() => savePin(team.id), 0); }}
                        className="text-red-300/70 hover:text-red-300 text-[11px] font-h uppercase tracking-wider"
                        title={t('ujp_remove_pin_title')}
                      >
                        {t('ujp_remove')}
                      </button>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-gray-500 mt-2">
          {t('ujp_footer')}
        </p>
      </div>
    </div>
  );
}
